/**
 * @fileoverview Small coloured chip used to label which managed agent emitted
 * a given activity log row inside the unified feed.
 * @module shared-panels/AgentChip
 *
 * Purpose:
 *   Replace the literal `[AssetList]` / `[Indicator]` text prefix with a
 *   compact, deterministic-coloured pill so each agent reads as a distinct
 *   visual identity across rows.
 */

import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";

// Stable colour palette — excludes red/destructive and gray (used elsewhere
// for state). Each agent always lands on the same colour because we hash on
// the agent name.
const AGENT_COLORS: BadgeColor[] = [
  "blue",
  "emerald",
  "amber",
  "indigo",
  "purple",
  "pink",
  "orange",
  "violet",
  "sky",
  "teal",
];

/**
 * Hashes an agent name into a deterministic palette colour.
 * Uses djb2 — fast and consistent across renders.
 */
function colorForAgent(agent: string): BadgeColor {
  const hash = Array.from(agent).reduce(
    (acc, ch) => (acc * 33) ^ ch.charCodeAt(0),
    5381,
  );
  return AGENT_COLORS[Math.abs(hash) % AGENT_COLORS.length];
}

export function AgentChip({
  agent,
  className,
}: {
  agent: string;
  className?: string;
}) {
  return (
    <Badge
      color={colorForAgent(agent)}
      variant="soft"
      size="sm"
      className={cn(
        "shrink-0 px-1.5 py-0 text-[10px] font-medium tracking-tight",
        className,
      )}
    >
      {agent}
    </Badge>
  );
}
